// Core imports
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// Misc imports
import { useCancelJobMutation, useCreateJobMutation, useCreateJobsMutation, useLazyFetchJobsQuery } from '../../../store/jobs/jobsApi';
import { JOB_STATUSES } from '../../../constants/main';

// Constants
const POLLING_INTERVAL_MS = 3000;
const POLLING_RETRY_ATTEMPTS = 3;
const JOB_TERMINAL_STATUSES = [JOB_STATUSES.COMPLETED, JOB_STATUSES.FAILED, JOB_STATUSES.STOPPED];

/**
 * @typedef {object} Job
 * @property {string} id
 * @property {string} objectId
 * @property {string} status
 * @property {number} progress
 */

/**
 * Reusable hook to manage the full lifecycle of asynchronous background jobs.
 * It handles job creation (single or bulk), cancellation, and status polling.
 *
 * @param {string} orgId - The ID of the organization.
 * @param {string} type - The job type to track (e.g., 'DOCUMENT_SEGMENTOR').
 * @param {string} [objectId] - Optional: Filter jobs for a specific object ID.
 * @param {string} [userId] - Optional: Filter jobs for a specific user ID.
 * @param {(job: Job) => void} [onJobFinished] - Optional: Callback executed when a job completes or fails.
 * @returns {{
 * state: {
 * pollingJobs: Job[],
 * pollingJobsByObjectIdMap: Map<string, Job>,
 * isPolling: boolean,
 * isLoading: boolean,
 * error: any
 * },
 * actions: {
 * createAndTrackJobs: (jobsToCreate: Array<object>) => Promise<void>,
 * cancelJob: (jobIdOrIds: string | string[]) => Promise<void>
 * }
 * }}
 */
export const useJobPolling = ({
  orgId,
  type,
  objectId,
  userId,
  onJobFinished = () => {}
}) => {
  const [createJob, { isLoading: isCreatingSingle }] = useCreateJobMutation();
  const [createJobs, { isLoading: isCreatingBulk }] = useCreateJobsMutation();
  const [cancelJobMutation, { isLoading: isCanceling }] = useCancelJobMutation();
  const [fetchJobs] = useLazyFetchJobsQuery();

  const [trackedJobIds, setTrackedJobIds] = useState(new Set());
  const [pollingJobs, setPollingJobs] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);

  const onJobFinishedRef = useRef(onJobFinished);

  useEffect(() => {
    onJobFinishedRef.current = onJobFinished;
  }, [onJobFinished]);

  const fetchInitialJobsParameters = useMemo(() => ({
    orgId,
    urlParams: {
      ...(!!objectId && { objectId: objectId }),
      status: [JOB_STATUSES.STARTING, JOB_STATUSES.RUNNING],
      ...(!!userId && { userId: userId }),
      type: type
    }
  }), [orgId, userId, objectId]);

  const fetchCompletedJobsParameters = useMemo(() => ({
    ...fetchInitialJobsParameters,
    urlParams: {
      ...fetchInitialJobsParameters.urlParams,
      status: [...fetchInitialJobsParameters.urlParams.status, ...JOB_TERMINAL_STATUSES],
      jobIds: Array.from(trackedJobIds)
    }
  }), [fetchInitialJobsParameters, trackedJobIds]);

  const pollTrackedJobs = useCallback(async () => {
    if (trackedJobIds.size === 0) {
      setIsPolling(false);
      return;
    }

    for (let attempt = 1; attempt <= POLLING_RETRY_ATTEMPTS; attempt++) {
      try {
        const response = await fetchJobs(fetchCompletedJobsParameters).unwrap();
        const updatedJobsFromServer = response || [];

        const justFinishedJobs = updatedJobsFromServer.filter(job => JOB_TERMINAL_STATUSES.includes(job.status));
        const justFinishedJobIds = new Set(justFinishedJobs.map(job => job.id));

        if (justFinishedJobs.length > 0) {
          justFinishedJobs.forEach(job => onJobFinishedRef.current(job));
        }

        setPollingJobs(currentJobs => {
          const updatedJobsMap = new Map(updatedJobsFromServer.map(job => [job.id, job]));
          const mergedJobs = currentJobs.map(job => updatedJobsMap.get(job.id) || job);
          const nextPollingJobs = mergedJobs.filter(job => !justFinishedJobIds.has(job.id));
          return nextPollingJobs;
        });

        if (justFinishedJobIds.size > 0) {
          setTrackedJobIds(currentIds => {
            const nextIds = new Set(currentIds);
            justFinishedJobIds.forEach(id => nextIds.delete(id));
            return nextIds;
          });
        }

        return;
      } catch (e) {
        console.error(`Polling attempt ${attempt} failed:`, e);
        if (attempt === POLLING_RETRY_ATTEMPTS) {
          setError(e);
          setIsPolling(false);
        }
      }
    }
  }, [orgId, type, trackedJobIds, fetchJobs]);

  useEffect(() => {
    const findInitialJobs = async () => {
      try {
        const response = await fetchJobs(fetchInitialJobsParameters).unwrap();

        const initialJobs = response || [];
        if (initialJobs.length > 0) {
          setPollingJobs(initialJobs);
          setTrackedJobIds(new Set(initialJobs.map(job => job.id)));
        }
      } catch (e) {
        console.error('Failed to fetch initial jobs:', e);
        setError(e);
      }
    };
    findInitialJobs();
  }, [orgId, type, objectId, userId]);

  useEffect(() => {
    const shouldPoll = trackedJobIds.size > 0;
    setIsPolling(shouldPoll);

    if (!shouldPoll) {
      return;
    }

    const intervalId = setInterval(pollTrackedJobs, POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [trackedJobIds, pollTrackedJobs]);

  const createAndTrackJobs = useCallback(async (jobsToCreate) => {
    setError(null);
    if (!jobsToCreate || jobsToCreate.length === 0) {
      return;
    }

    for (const job of jobsToCreate) {
      if (!job.objectId || !job.type) {
        const validationError = new Error('Each job must have an objectId and type.');
        setError(validationError);
        throw validationError;
      }
    }

    try {
      const response = jobsToCreate.length === 1
        ? await createJob({ orgId, data: jobsToCreate[0] }).unwrap()
        : await createJobs({ orgId, data: jobsToCreate }).unwrap().then(async results => {
          const newJobs = await fetchJobs(
            {
              orgId,
              urlParams: {
                type,
                status: [JOB_STATUSES.RUNNING, JOB_STATUSES.STARTING]
              }
            }
          ).unwrap();

          const createdJobs = newJobs?.filter(job => jobsToCreate.map(createdJob => createdJob?.objectId).includes(job?.objectId));

          return createdJobs;
        });

      const newJobs = Array.isArray(response) ? response : [response];

      setTrackedJobIds(prevIds => new Set([...prevIds, ...newJobs.map(job => job.id)]));
      setPollingJobs(prevJobs => [...prevJobs, ...newJobs]);

    } catch (e) {
      console.error('Failed to create job(s):', e);
      setError(e);
      throw e;
    }
  }, [orgId, createJob, createJobs]);

  const cancelJob = useCallback(async (jobIdOrIds) => {
    setError(null);
    const idsToCancel = Array.isArray(jobIdOrIds) ? jobIdOrIds : [jobIdOrIds];

    try {
      await Promise.all(
        idsToCancel.map(id => cancelJobMutation({ orgId, id }).unwrap())
      );
    } catch (e) {
      console.error('Failed to cancel job(s):', e);
      setError(e);
      throw e;
    }
  }, [orgId, cancelJobMutation]);

  const pollingJobsByObjectIdMap = useMemo(() =>
    new Map(pollingJobs.map(job => [job.objectId, job])),
  [pollingJobs]
  );

  const isLoading = isCreatingSingle || isCreatingBulk || isCanceling;

  return {
    state: {
      pollingJobs,
      pollingJobsByObjectIdMap,
      isPolling,
      isLoading,
      error
    },
    actions: {
      createAndTrackJobs,
      cancelJob
    }
  };
};
